import React from "react";
import type { SponsoredAdSlot } from "../../../Redux/ecommersProduct/types";
import type { AdPlacement as AdPlacementType, AdType } from "../../../Redux/storefront-ads/types";
import useAdSlots from "../../../hooks/useAdSlots";
import SponsoredProductCard from "../product/SponsoredProductCard";
import SponsoredBrandSection from "../../pages/productlisting/categorie/SponsoredBrandSection";
import SponsoredDisplaySection from "../../pages/productlisting/categorie/SponsoredDisplaySection";

type AdPlacementProps = {
  placement: AdPlacementType;
  adTypes?: AdType[];
  limit?: number;
  searchQuery?: string;
  categoryType?: string;
  productId?: string;
  /** Render only specific ad types */
  show?: AdType | AdType[];
  className?: string;
};

const matchesShow = (adType: string, show?: AdType | AdType[]) => {
  if (!show) return true;
  const allowed = Array.isArray(show) ? show : [show];
  return allowed.includes(adType as AdType);
};

const renderSlot = (slot: SponsoredAdSlot, key: string, placement: AdPlacementType) => {
  switch (slot.adType) {
    case "product":
      return (
        <div className="row" key={key}>
          <SponsoredProductCard slot={slot} />
        </div>
      );
    case "sponsored_brand":
      return <SponsoredBrandSection key={key} slot={slot} />;
    case "display":
      return (
        <SponsoredDisplaySection
          key={key}
          slot={slot}
          layout={placement === "home_feed" ? "banner" : "sidebar"}
        />
      );
    default:
      return null;
  }
};

/**
 * Unified ad placement component — fetches slots via BFF and renders
 * the correct Amazon-style widget per ad type.
 */
const AdPlacement: React.FC<AdPlacementProps> = ({
  placement,
  adTypes,
  limit,
  searchQuery,
  categoryType,
  productId,
  show,
  className = "",
}) => {
  const { slots, loading } = useAdSlots({
    placement,
    adTypes,
    limit,
    searchQuery,
    categoryType,
    productId,
  });

  const visible = slots.filter((s) => matchesShow(s.adType, show));
  if (loading && visible.length === 0) return null;
  if (visible.length === 0) return null;

  return (
    <div className={className}>
      {visible.map((slot, i) =>
        renderSlot(slot, `${slot.impressionId ?? slot.campaignId}-${i}`, placement),
      )}
    </div>
  );
};

export default AdPlacement;
